import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';

import { Flex } from '@dynatrace/strato-components/layouts';

import {
  generateColumnVisibilityObject,
  getCombinedVisibilityCheckboxState,
  getHasColumnOrderChanged,
  getHasVisibilityChanged,
  updateMultipleLocalColumnVisibility,
} from './column-settings-utils.js';
import { DataTableV2ColumnsList } from './DataTableV2ColumnsList.js';
import { DataTableV2VisibilityFilterControls } from './DataTableV2VisibilityFilterControls.js';
import { useDataTableV2 } from '../../../contexts/DataTableV2Context.js';
import type { DataTableV2ColumnVisibilityState } from '../../../features/ColumnVisibility/ColumnVisibility.js';
import type { Column } from '../../../hooks/useTable/types.js';
import { dataTableMessages } from '../../../messages.js';
import { DataTableV2ColumnSettingsConfig } from '../../../public.api.js';
import { DataTableV2SettingsModal } from '../../SettingsModal/DataTableV2SettingsModal.js';

/**
 * ref type of the column settings modal
 * @internal
 */
export interface DataTableV2ColumnSettingsModalRef {
  /**
   * Opens the column settings modal.
   *
   * @param columnSettings - specifies which column settings are available in the modal.
   * Every setting not explicitly specified defaults to its respective slot in the `DataTableV2Toolbar`.
   */
  openColumnSettings: (
    columnSettings?: DataTableV2ColumnSettingsConfig,
  ) => void;
}

interface DataTableV2ColumnSettingsModalProps {
  /**
   * Whether column visibility settings are available in the modal.
   */
  visibilitySettings: boolean;
  /**
   * Whether column order settings are available in the modal.
   */
  columnOrderSettings: boolean;
  /**
   * Default column visibility when settings are reset.
   */
  resetColumnVisibility?: DataTableV2ColumnVisibilityState;
  /**
   * Default column order when settings are reset.
   */
  resetColumnOrder?: string[];
}

/**
 * Modal combining UI controls for column visibility and column ordering.
 * All changes that made within the modal are only applied once the user click the Confirm button.
 * When clicking Close, the changes won't be applied and discarded.
 */
const ColumnSettingsModal = (
  props: DataTableV2ColumnSettingsModalProps & {
    onModalClose: () => void;
    columnSettingsOverride?: DataTableV2ColumnSettingsConfig;
  },
) => {
  const {
    visibilitySettings: toolbarVisibilitySettings,
    columnOrderSettings: toolbarColumnOrderSettings,
    resetColumnOrder,
    resetColumnVisibility,
    onModalClose,
    columnSettingsOverride,
  } = props;

  const intl = useIntl();
  const table = useDataTableV2();
  const leafColumnsWithoutBuiltin = table.getLeafColumnsWithoutBuiltin();

  const visibilitySettings =
    columnSettingsOverride?.columnVisibility ?? toolbarVisibilitySettings;
  const columnOrderSettings =
    columnSettingsOverride?.columnOrder ?? toolbarColumnOrderSettings;

  // original column visibility state
  const getTableColumnVisibility = () =>
    generateColumnVisibilityObject(table.getLeafColumnsWithoutBuiltin());
  const tableColumnVisibility = useMemo(getTableColumnVisibility, [
    table.options.columns,
  ]);

  // original column order state
  const getTableColumnOrder = () =>
    table.getState().columnOrder ||
    table.getLeafColumnsWithoutBuiltin().map((col) => col.id);
  const tableColumnOrder = useMemo(getTableColumnOrder, [
    table.options.columns,
  ]);

  const [activeFilterValue, setActiveFilterValue] = useState<string>('');

  // current column visibility state while modal is open
  const [localColumnVisibility, setLocalColumnVisibility] = useState<
    Record<string, boolean>
  >(tableColumnVisibility);

  // current column order state while modal is open
  const [localColumnOrder, setLocalColumnOrder] =
    useState<string[]>(tableColumnOrder);

  // update states when column definition changes
  useEffect(() => {
    setLocalColumnVisibility(tableColumnVisibility);
    setLocalColumnOrder(tableColumnOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.options.columns]);

  // if controlled, call the callback to get resettable state. If that doesn't work or uncontrolled
  // use the evaluated original table default order/visibility state from options
  const columnOrderToReset =
    resetColumnOrder ?? table.options.originalColumnOrder;
  const columnVisibilityToReset =
    resetColumnVisibility ?? table.options.originalColumnVisibility;

  // checks if local default column visibility has changed
  const hasVisibilityChanged =
    visibilitySettings &&
    getHasVisibilityChanged(columnVisibilityToReset, localColumnVisibility);

  // checks if local default column order has changed
  const hasColumnOrderChanged =
    columnOrderSettings &&
    getHasColumnOrderChanged(columnOrderToReset, localColumnOrder);

  // determines whether all columns are visible
  const showHideAllState = getCombinedVisibilityCheckboxState(
    localColumnVisibility,
  );

  const getVisibilityCheckboxState = (column: Column<unknown, unknown>) => {
    const childColumns = column.columns;
    if (childColumns.length === 0) {
      return localColumnVisibility[column.id];
    } else {
      // if the column is a parent column, we need to consider all child columns to determine its visibility state
      const childrenVisibility = generateColumnVisibilityObject(
        childColumns,
        localColumnVisibility,
      );
      return getCombinedVisibilityCheckboxState(childrenVisibility);
    }
  };

  const updateLocalColumnVisibility = (
    column: Column<unknown, unknown>,
    currentVisibilityState: boolean | 'indeterminate',
  ) => {
    const childColumns = column.columns;
    if (childColumns.length === 0) {
      setLocalColumnVisibility({
        ...localColumnVisibility,
        [column.id]: !currentVisibilityState,
      });
    } else {
      updateMultipleLocalColumnVisibility(
        childColumns,
        currentVisibilityState,
        localColumnVisibility,
        setLocalColumnVisibility,
      );
    }
  };

  const onShowHideAllChange = () =>
    updateMultipleLocalColumnVisibility(
      leafColumnsWithoutBuiltin,
      showHideAllState,
      localColumnVisibility,
      setLocalColumnVisibility,
    );

  const onReset = () => {
    // reset visibility and/or order if they are enabled
    if (visibilitySettings && columnVisibilityToReset) {
      setLocalColumnVisibility(
        generateColumnVisibilityObject(
          leafColumnsWithoutBuiltin,
          columnVisibilityToReset,
        ),
      );
    }
    if (columnOrderSettings && columnOrderToReset) {
      setLocalColumnOrder(columnOrderToReset);
    }
  };

  const onApply = () => {
    table.setColumnVisibility(localColumnVisibility);
    table.setColumnOrder(localColumnOrder);
    onModalClose();
  };

  return (
    <DataTableV2SettingsModal
      title={intl.formatMessage({
        defaultMessage: 'Columns',
        id: 'tznlZbNaiUJ1dPqb',
        description: 'Column settings modal title',
      })}
      show={true}
      onDismiss={onModalClose}
      onReset={onReset}
      onApply={onApply}
      disableButtons={{
        reset: !hasColumnOrderChanged && !hasVisibilityChanged,
      }}
      controlLabels={{
        reset: intl.formatMessage(
          dataTableMessages.resetColumnSettingsButtonText,
        ),
        cancel: intl.formatMessage(dataTableMessages.cancelButtonText),
        apply: intl.formatMessage(dataTableMessages.applyButtonText),
      }}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        marginBottom={24}
      >
        {visibilitySettings && (
          <DataTableV2VisibilityFilterControls
            activeFilterValue={activeFilterValue}
            setActiveFilterValue={setActiveFilterValue}
            showHideAllState={showHideAllState}
            onShowHideAllChange={onShowHideAllChange}
          />
        )}
      </Flex>
      <DataTableV2ColumnsList
        visibilitySettings={visibilitySettings}
        columnOrderSettings={columnOrderSettings}
        activeFilterValue={activeFilterValue}
        getVisibilityCheckboxState={getVisibilityCheckboxState}
        updateLocalColumnVisibility={updateLocalColumnVisibility}
        localColumnOrder={localColumnOrder}
        setLocalColumnOrder={setLocalColumnOrder}
      />
    </DataTableV2SettingsModal>
  );
};

function DataTableV2ColumnSettingsModalFwd(
  props: DataTableV2ColumnSettingsModalProps,
  forwardedRef: Ref<DataTableV2ColumnSettingsModalRef>,
) {
  const [modalOpen, setModalOpen] = useState(false);
  const [columnSettingsOverride, setColumnSettingsOverride] =
    useState<DataTableV2ColumnSettingsConfig>();

  // open the modal from outside
  useImperativeHandle(forwardedRef, () => ({
    openColumnSettings: (columnSettings?: DataTableV2ColumnSettingsConfig) => {
      setColumnSettingsOverride(columnSettings);
      setModalOpen(true);
    },
  }));

  if (modalOpen) {
    return (
      <ColumnSettingsModal
        {...props}
        columnSettingsOverride={columnSettingsOverride}
        onModalClose={() => setModalOpen(false)}
      />
    );
  }
  return null;
}

/**
 * @internal
 */
export const DataTableV2ColumnSettingsModal = forwardRef(
  DataTableV2ColumnSettingsModalFwd,
);
